import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { downloadBlob } from '@/lib/download';
import { toApiProblem } from '@/lib/errors';
import { labelArchiveFilename, labelFilename } from '@/lib/labels';
import { withQuery } from '@/lib/qs';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { LabelFormat, Paginated } from '../types/common';
import type {
  BatchCreateShipmentsPayload,
  CreateShipmentPayload,
  Shipment,
  ShipmentBatch,
  ShipmentFilters,
} from '../types/shipments';

import { useMe } from './merchant-settings';

type Wrapped<T> = { data: T };

export function useShipments(filters: ShipmentFilters) {
  const api = useApiClient();
  return useQuery<Paginated<Shipment>, Error>({
    queryKey: queryKeys.shipments.list(filters),
    queryFn: ({ signal }) => api.get(withQuery('/admin/shipments', filters), signal),
  });
}

export function useCreateShipment(orderId: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<Shipment, Error, CreateShipmentPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<Shipment>>(
        `/admin/orders/${orderId}/shipments`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.orders.all });
      void qc.invalidateQueries({ queryKey: queryKeys.orderShipments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.shipments.all });
    },
  });
}

export function useBatchCreateShipments() {
  const api = useApiClient();
  return useMutation<ShipmentBatch, Error, BatchCreateShipmentsPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<ShipmentBatch>>('/admin/shipments/batch', payload);
      return response.data;
    },
  });
}

/**
 * Polls a batch's per-order progress while it is still being processed in the
 * background. Stops polling once the batch reaches `COMPLETED`.
 */
export function useShipmentBatch(id: number | null) {
  const api = useApiClient();
  return useQuery<ShipmentBatch, Error>({
    queryKey: queryKeys.shipmentBatches.detail(id),
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<ShipmentBatch>>(
        `/admin/shipments/batches/${id}`,
        signal,
      );
      return response.data;
    },
    // Poll while processing, but stop on a persistent error so a dropped network
    // (last data still says PROCESSING) doesn't loop forever.
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.status === 'PROCESSING' ? 1500 : false,
  });
}

const DEFAULT_LABEL_FORMAT: LabelFormat = 'application/pdf;format=A6';

/**
 * Downloads labels for one or more shipments. Labels are fetched live from the
 * carrier in the requested format (defaults to the account's configured format);
 * a single shipment streams the raw label named `shipment-{carrier_shipment_id}.{ext}`,
 * multiple shipments stream a `labels_{datetime}.zip` archive. The backend pivots
 * raw-vs-zip on the number of requested ids, so the filename is derived from the
 * same count here. A freshly-created shipment can return 425 (label not generated
 * yet) - that case is retried automatically with backoff.
 */
export function useDownloadLabels() {
  const api = useApiClient();
  const me = useMe();
  const defaultFormat = (me.data?.settings.default_label_format ??
    DEFAULT_LABEL_FORMAT) as LabelFormat;

  return useMutation<void, Error, { shipments: Shipment[]; format?: LabelFormat }>({
    mutationFn: async ({ shipments, format }) => {
      const labelFormat = format ?? defaultFormat;
      const ids = shipments.map((shipment) => shipment.id);
      const blob = await api.blob(
        withQuery('/admin/shipments/labels', { ids, format: labelFormat }),
      );
      const filename =
        shipments.length === 1
          ? labelFilename(shipments[0], labelFormat)
          : labelArchiveFilename(labelFormat);
      downloadBlob(blob, filename);
    },
    retry: (failureCount, error) =>
      failureCount < 3 && toApiProblem(error).type === 'inpost_label_not_ready',
    retryDelay: (failureCount) => Math.min(1000 * 2 ** failureCount, 8000),
  });
}
